(() => {
  const root = document.querySelector("[data-home-categories]");
  if (!root) return;

  const DAY = 86400;
  const LIMIT = 16;

  function escapeHtml(value) {
    return String(value || "").replace(/[&<>"']/g, (char) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;"
    }[char]));
  }

  function parseJsonPayload(text) {
    const start = text.indexOf("{");
    const end = text.lastIndexOf("}");
    if (start < 0 || end < start) return { products: [] };
    return JSON.parse(text.slice(start, end + 1));
  }

  function unix(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }

  function discount(product) {
    const price = Number(product.price) || 0;
    const compare = Number(product.compare_at) || 0;
    if (!compare || compare <= price) return 0;
    return (compare - price) / compare;
  }

  function tagsOf(product) {
    return (product.tags || []).map((tag) => String(tag).toLowerCase());
  }

  function tagged(product, pattern) {
    return tagsOf(product).some((tag) => pattern.test(tag));
  }

  function sortBy(list, key, dir = -1) {
    return [...list].sort((a, b) => (unix(a[key]) - unix(b[key])) * dir);
  }

  function preferAvailable(list) {
    return [
      ...list.filter((product) => product.available),
      ...list.filter((product) => !product.available)
    ];
  }

  function take(list, count = LIMIT) {
    return preferAvailable(list).slice(0, count);
  }

  function uniqueByHandle(list) {
    const seen = new Set();
    return list.filter((item) => {
      if (!item.handle || seen.has(item.handle)) return false;
      seen.add(item.handle);
      return true;
    });
  }

  function bucket(products, source) {
    const now = Math.floor(Date.now() / 1000);
    const onSale = products.filter((product) => discount(product) > 0);
    const live = products.filter((product) => product.available && !/discontinued/i.test(product.title || ""));

    if (source === "new_arrivals") {
      const taggedNew = products.filter((product) => tagged(product, /latest.?arrival|new.?arrival/));
      if (taggedNew.length) return take(sortBy(taggedNew, "created_at"));
      return take(sortBy(live, "created_at"));
    }

    if (source === "newly_on_sale") {
      const taggedSale = products.filter((product) => tagged(product, /weekly.?sale|special.?discount|newly.?on.?sale/));
      if (taggedSale.length) return take(sortBy(taggedSale, "updated_at"));
      return take(sortBy(onSale, "updated_at"));
    }

    if (source === "clearance") {
      const taggedClearance = products.filter((product) => tagged(product, /clearance|final.?sale|markdown/));
      if (taggedClearance.length) {
        return take(taggedClearance.sort((a, b) => discount(b) - discount(a) || unix(b.updated_at) - unix(a.updated_at)));
      }
      const deep = onSale.filter((product) => discount(product) >= 0.2);
      const ranked = uniqueByHandle([...deep, ...onSale])
        .sort((a, b) => discount(b) - discount(a) || unix(b.updated_at) - unix(a.updated_at));
      return take(ranked);
    }

    if (source === "back_in_stock") {
      const taggedRestock = live.filter((product) => tagged(product, /back.?in.?stock|restock|restocked/));
      if (taggedRestock.length) return take(sortBy(taggedRestock, "updated_at"));

      const restocked = live.filter((product) => {
        if (tagged(product, /latest.?arrival|new.?arrival|clearance/)) return false;
        const created = unix(product.created_at);
        const updated = unix(product.updated_at);
        return created && updated && created < now - 21 * DAY && updated > now - 45 * DAY && updated - created > 7 * DAY;
      });
      if (restocked.length) return take(sortBy(restocked, "updated_at"));
      return take(sortBy(live.filter((product) => !tagged(product, /latest.?arrival|new.?arrival/)), "updated_at"));
    }

    return take(products);
  }

  function cardHTML(row) {
    if (window.themeCard?.html) return window.themeCard.html(row);
    return `<article class="product-card"><a href="${escapeHtml(row.url)}"><h3>${escapeHtml(row.title)}</h3></a></article>`;
  }

  function syncNav(wrap) {
    const track = wrap.querySelector("[data-rail-track]");
    const prev = wrap.querySelector("[data-rail-prev]");
    const next = wrap.querySelector("[data-rail-next]");
    if (!track || !prev || !next) return;
    const max = track.scrollWidth - track.clientWidth - 4;
    const overflow = max > 8;
    prev.hidden = !overflow;
    next.hidden = !overflow;
    prev.disabled = track.scrollLeft <= 8;
    next.disabled = track.scrollLeft >= max;
  }

  function bindRail(wrap) {
    const track = wrap.querySelector("[data-rail-track]");
    if (!wrap || !track || wrap.dataset.railBound) return;
    wrap.dataset.railBound = "1";
    const step = () => Math.max(track.clientWidth * 0.85, 220);
    wrap.querySelector("[data-rail-prev]")?.addEventListener("click", () => {
      track.scrollBy({ left: -step(), behavior: "smooth" });
    });
    wrap.querySelector("[data-rail-next]")?.addEventListener("click", () => {
      track.scrollBy({ left: step(), behavior: "smooth" });
    });
    track.addEventListener("scroll", () => syncNav(wrap), { passive: true });
    window.addEventListener("resize", () => syncNav(wrap));
    syncNav(wrap);
  }

  window.themeRails = { sync: syncNav, bind: bindRail };
  document.querySelectorAll(".product-rail-wrap").forEach(bindRail);
  document.querySelectorAll("[data-rail-track], [data-filter-grid]").forEach(stockOrderTrack);

  function paint(block, products) {
    const track = block.querySelector("[data-rail-track]");
    if (!track) return;
    if (!products.length) {
      block.hidden = true;
      return;
    }
    block.hidden = false;
    track.innerHTML = preferAvailable(products).map(cardHTML).join("");
    syncNav(block.querySelector(".product-rail-wrap"));
  }

  function stockOrderTrack(track) {
    if (!track) return;
    const cards = [...track.querySelectorAll(".product-card")];
    const live = cards.filter((card) => !card.classList.contains("is-sold-out"));
    const sold = cards.filter((card) => card.classList.contains("is-sold-out"));
    [...live, ...sold].forEach((card) => track.appendChild(card));
  }

  const autoRails = [...root.querySelectorAll("[data-rail]:not([data-rail-static])")];
  if (!autoRails.length) return;

  const url = window.theme?.catalogUrl;
  if (!url) return;

  fetch(url)
    .then((res) => res.text())
    .then((text) => parseJsonPayload(text).products || [])
    .then((products) => {
      autoRails.forEach((block) => {
        paint(block, bucket(products, block.dataset.railSource));
      });
    })
    .catch(() => {});
})();
